import React, { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CardContainer, CardBody, CardItem } from './ui/3d-card';
import { HeartIcon as HeartIconOutline, ShoppingCartIcon, ArrowRightIcon, EyeIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import { useFavorites } from '../context/FavoritesContext';
import { useCart } from '../context/CartContext';
import { useImageSlider } from '../hooks/useImageSlider';
import { getOptimizedImageUrl, getResponsiveImageSet } from '../utils/imageOptimizer';
import api from '../utils/api';
import toast from 'react-hot-toast';
import QuickViewModal from './QuickViewModal';

// CSS animasyonları için style component
const ProductCardStyles = () => (
  <style>{`
    @keyframes sparkle {
      0%, 100% { transform: scale(1) rotate(0deg); }
      25% { transform: scale(1.05) rotate(5deg); }
      50% { transform: scale(1.1) rotate(-5deg); }
      75% { transform: scale(1.05) rotate(5deg); }
    }
    
    @keyframes slideIn {
      0% { opacity: 0; transform: translateY(20px); }
      100% { opacity: 1; transform: translateY(0); }
    }
    
    .sparkle-animation {
      animation: sparkle 2s infinite;
    }
  `}</style>
);

const ProductCard = ({ product }) => {
  const navigate = useNavigate();
  const { toggleFavorite, isProductFavorited } = useFavorites();
  const { addToCart } = useCart();
  const [categoryDiscount, setCategoryDiscount] = useState(null);
  const [packageDeal, setPackageDeal] = useState(null);
  const [specialDayDiscount, setSpecialDayDiscount] = useState(null);
  const [flashSaleDiscount, setFlashSaleDiscount] = useState(null);
  const [cartCampaign, setCartCampaign] = useState(null); // Başlangıçta kesinlikle null
  const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);

  // Debug log
  // console.log('🛒 ProductCard Render:', {
  //   productId: product?.id,
  //   productName: product?.name,
  //   cartCampaignState: cartCampaign,
  //   hasCartCampaignId: cartCampaign?.id ? 'VAR' : 'YOK'
  // });

  const allImages = useMemo(() => {
    if (!product) return [];
    const images = [product.main_image_url, ...(product.gallery_image_urls || [])];
    const filteredImages = [...new Set(images)].filter(Boolean);
    
    // Debug log - resim URL'lerini kontrol et
    // console.log('🖼️ ProductCard Images Debug:', {
    //   productId: product.id,
    //   productName: product.name,
    //   mainImageUrl: product.main_image_url,
    //   galleryImages: product.gallery_image_urls,
    //   allImages: images,
    //   filteredImages: filteredImages
    // });
    
    return filteredImages;
  }, [product]);

  // Kartlar için geçiş süresini daha hızlı tutabiliriz, örneğin 1.2 saniye
  const { currentIndex, startSlider, stopSlider } = useImageSlider(allImages, 1200);

  // Kategori indirimini kontrol et - YENİ BİRLEŞİK API
  useEffect(() => {
    const checkCategoryDiscount = async () => {
      if (!product || !product.id) return;
      
      // console.log('🔍 Yeni birleşik API ile kategori indirimi kontrol ediliyor:', product.name, product.id);
      
      try {
        // Yeni birleşik API endpoint'ini kullan
        const response = await api.post('/api/campaigns/check-unified-category-discounts', {
          cartItems: [{
            id: product.id,
            name: product.name,
            price: product.price,
            category: product.category,
            quantity: 1,
            variant: { price_override: null }
          }]
        });

        // console.log('📦 Birleşik kategori indirimi API yanıtı:', response);

        if (response.success && response.data.hasDiscounts && response.data.applicableDiscounts.length > 0) {
          const discount = response.data.applicableDiscounts[0];
          // console.log('✅ Kategori indirimi bulundu:', discount, 'Method:', response.data.method);
          
          // AffectedItems'den ilk ürünün indirim bilgisini al
          const affectedItem = discount.affectedItems && discount.affectedItems.length > 0 
            ? discount.affectedItems[0] 
            : null;
            
          setCategoryDiscount({
            campaignName: discount.campaignName,
            badgeText: discount.badgeText,
            badgeColor: discount.badgeColor,
            discountAmount: affectedItem ? affectedItem.discountAmount : discount.totalCampaignDiscount,
            discountType: discount.discountType,
            discountValue: discount.discountValue,
            method: response.data.method // Hangi yöntemin kullanıldığını izle
          });
        } else {
          // console.log('❌ Kategori indirimi bulunamadı:', product.name, 'Method:', response.data?.method);
          setCategoryDiscount(null);
        }
      } catch (error) {
        console.error('❌ Birleşik kategori indirimi kontrol hatası:', error);
        
        // Fallback: Eski API'yi dene
        // console.log('🔄 Fallback: Eski simple API deneniyor...');
        try {
          const fallbackResponse = await api.post('/api/campaigns/check-simple-category-discounts', {
            cartItems: [{
              id: product.id,
              name: product.name,
              price: product.price,
              category: product.category,
              quantity: 1,
              variant: { price_override: null }
            }]
          });

          if (fallbackResponse.success && fallbackResponse.data.hasDiscounts) {
            const discount = fallbackResponse.data.applicableDiscounts[0];
            setCategoryDiscount({
              campaignName: discount.campaignName,
              badgeText: discount.badgeText,
              badgeColor: discount.badgeColor,
              discountAmount: discount.totalCampaignDiscount,
              discountType: discount.discountType,
              discountValue: discount.discountValue,
              method: 'fallback_simple'
            });
          } else {
            setCategoryDiscount(null);
          }
        } catch (fallbackError) {
          console.error('❌ Fallback API de başarısız:', fallbackError);
          setCategoryDiscount(null);
        }
      }
    };

    const checkPackageDeals = async () => {
      if (!product || !product.id) return;
      
      // console.log('📦 ProductCard: Paket kampanyası kontrol ediliyor:', product.name);
      
      try {
        const response = await api.post('/api/campaigns/check-package-deals', {
          cartItems: [{
            id: product.id,
            name: product.name,
            price: product.price,
            category: product.category,
            quantity: 2, // 2 adet varsayarak kontrol et
            variant: { price_override: null }
          }]
        });

        if (response.success && response.data.hasDiscounts && response.data.applicableDiscounts.length > 0) {
          const deal = response.data.applicableDiscounts[0];
          // console.log('✅ ProductCard: Paket kampanyası bulundu:', deal);
          setPackageDeal({
            campaignName: deal.campaignName,
            badgeText: deal.badgeText,
            badgeColor: deal.badgeColor,
            buyCount: deal.buyCount,
            payCount: deal.payCount,
            packageType: deal.packageType
          });
        } else {
          setPackageDeal(null);
        }
      } catch (error) {
        console.error('❌ ProductCard: Paket kampanyası kontrol hatası:', error);
        setPackageDeal(null);
      }
    };

    const checkSpecialDayDiscounts = async () => {
      if (!product || !product.id) return;
      
      // console.log('🎉 ProductCard: Özel gün kampanyası kontrol ediliyor:', product.name);
      
      try {
        const response = await api.post('/api/campaigns/check-special-day-discounts', {
          cartItems: [{
            id: product.id,
            name: product.name,
            price: product.price,
            category: product.category,
            quantity: 1,
            variant: { price_override: null }
          }],
          cartTotal: product.price,
          userInfo: null // Ürün kartında kullanıcı bilgisi yok
        });

        if (response.success && response.data.hasDiscounts && response.data.applicableDiscounts.length > 0) {
          const discount = response.data.applicableDiscounts[0];
          // console.log('✅ ProductCard: Özel gün kampanyası bulundu:', discount);
          setSpecialDayDiscount({
            campaignName: discount.campaignName,
            badgeText: discount.badgeText,
            badgeColor: discount.badgeColor,
            specialDayType: discount.specialDayType,
            discountAmount: discount.discountAmount,
            targetAudience: discount.targetAudience
          });
        } else {
          setSpecialDayDiscount(null);
        }
      } catch (error) {
        console.error('❌ ProductCard: Özel gün kampanyası kontrol hatası:', error);
        setSpecialDayDiscount(null);
      }
    };

    const checkFlashSaleDiscounts = async () => {
      if (!product || !product.id) return;
      
      // console.log('⚡ ProductCard: Flash Sale kontrol ediliyor:', product.name);
      
      try {
        const response = await api.get('/api/flash-sale/active');
        
        if (response.success && response.data && response.data.length > 0) {
          // Bu ürünün flash sale'de olup olmadığını kontrol et
          const arr = Array.isArray(response.data) ? response.data : (Array.isArray(response) ? response : []);
          const productFlashSale = arr.find(flashSale => 
            flashSale.products && flashSale.products.some(p => String(p.id) === String(product.id))
          );
          
          if (productFlashSale) {
            const productInSale = productFlashSale.products.find(p => String(p.id) === String(product.id));
            const toNum = (v) => {
              const n = typeof v === 'string' ? parseFloat(v.replace(',', '.')) : Number(v);
              return isNaN(n) ? 0 : n;
            };
            const basePrice = toNum(product.price);
            const discountValueRaw = (productInSale && productInSale.custom_discount_value != null)
              ? productInSale.custom_discount_value
              : productFlashSale.discount_value;
            const discountValue = toNum(discountValueRaw);
            
            let discountAmount = 0;
            if (productFlashSale.discount_type === 'percentage') {
              if (discountValue > 0) {
                discountAmount = (basePrice * discountValue) / 100;
              } else if (toNum(productInSale?.discount_percentage) > 0) {
                discountAmount = (basePrice * toNum(productInSale.discount_percentage)) / 100;
              } else if (toNum(productInSale?.discounted_price) > 0) {
                discountAmount = Math.max(0, basePrice - toNum(productInSale.discounted_price));
              }
            } else {
              // fixed amount veya eksik değer fallback
              discountAmount = discountValue > 0
                ? discountValue
                : Math.max(0, basePrice - toNum(productInSale?.discounted_price));
            }
            
            // console.log('✅ ProductCard: Flash Sale bulundu:', productFlashSale.name);
            setFlashSaleDiscount({
              campaignName: productFlashSale.name,
              badgeText: productFlashSale.badge_text || (discountValue > 0 ? `FLASH %${discountValue}` : 'FLASH SALE'),
              badgeColor: productFlashSale.badge_color || '#ff4444',
              discountAmount: toNum(discountAmount),
              discountType: productFlashSale.discount_type,
              discountValue: discountValue > 0 ? discountValue : (toNum(productInSale?.discount_percentage) || null),
              endDate: productFlashSale.end_date
            });
          } else {
            setFlashSaleDiscount(null);
          }
        } else {
          setFlashSaleDiscount(null);
        }
      } catch (error) {
        console.error('❌ ProductCard: Flash Sale kontrol hatası:', error);
        setFlashSaleDiscount(null);
      }
    };

    const checkCartCampaigns = async () => {
      if (!product || !product.id) return;
      
      try {
        // console.log('🛒 ProductCard: Sepet kampanyası API çağrısı yapılıyor...');
        const response = await api.get('/api/campaigns/active-cart-campaigns');
        
        // console.log('🛒 ProductCard: Ham API yanıtı:', response);
        // console.log('🛒 ProductCard: response.data:', response.data);
        // console.log('🛒 ProductCard: response.data türü:', typeof response.data);
        
        // API'den başarılı yanıt geldi ve aktif kampanya var mı kontrol et
        if (response.success && response.data && Array.isArray(response.data) && response.data.length > 0) {
          // Sadece gerçekten aktif olan kampanyaları filtrele
          const activeCampaigns = response.data.filter(campaign => {
            const now = new Date();
            const startDate = new Date(campaign.start_date);
            const endDate = new Date(campaign.end_date);
            
            // console.log('🛒 Kampanya kontrolü:', {
            //   campaignName: campaign.name,
            //   status: campaign.status,
            //   startDate: startDate.toISOString(),
            //   endDate: endDate.toISOString(),
            //   now: now.toISOString(),
            //   minCartAmount: campaign.min_cart_amount,
            //   discountValue: campaign.discount_value,
            //   discountType: campaign.discount_type
            // });
            
            return campaign.status === 'active' && 
                   now >= startDate && 
                   now <= endDate;
          });
          
          // console.log('🛒 Filtrelenmiş aktif kampanyalar:', activeCampaigns);
          
          if (activeCampaigns.length > 0) {
            const activeCampaign = activeCampaigns[0];
            // console.log('🛒 ProductCard: Aktif sepet kampanyası bulundu:', activeCampaign);
            
            // Backend'den gelen snake_case verileri camelCase'e çevir
            setCartCampaign({
              id: activeCampaign.id,
              name: activeCampaign.name,
              description: activeCampaign.description,
              discountType: activeCampaign.discount_type,
              discountValue: activeCampaign.discount_value,
              minCartAmount: activeCampaign.min_cart_amount,
              maxCartAmount: activeCampaign.max_cart_amount,
              badgeText: activeCampaign.badge_text || 'SEPET İNDİRİMİ',
              badgeColor: activeCampaign.badge_color || '#10b981',
              // Ayrıca orijinal verileri de sakla (fallback için)
              ...activeCampaign
            });
          } else {
            // console.log('🛒 ProductCard: Aktif sepet kampanyası bulunamadı (tarih/durum kontrolü)');
            setCartCampaign(null);
          }
        } else {
          // console.log('🛒 ProductCard: Aktif sepet kampanyası bulunamadı (API yanıtı boş)', {
          //   success: response.success,
          //   hasData: !!response.data,
          //   isArray: Array.isArray(response.data),
          //   length: response.data?.length
          // });
          setCartCampaign(null);
        }
      } catch (error) {
        console.error('❌ ProductCard: Sepet kampanyası kontrol hatası:', error);
        console.error('❌ ProductCard: Hata detayı:', error.message);
        setCartCampaign(null);
      }
    };

    checkCategoryDiscount();
    checkPackageDeals();
    checkSpecialDayDiscounts();
    checkFlashSaleDiscounts();
    checkCartCampaigns();
  }, [product]); 

  if (!product || !product.id) {
    return null;
  }

  const handleFavoriteClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleFavorite(product);
  };
  
  const handleQuickViewClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsQuickViewOpen(true);
  };
  
  const handleProductClick = (e, productId) => {
    e.preventDefault();
    navigate(`/products/${productId}`);
  };

  return (
    <>
      <ProductCardStyles />
      <QuickViewModal 
        product={product}
        isOpen={isQuickViewOpen}
        onClose={() => setIsQuickViewOpen(false)}
      />
      <CardContainer className="inter-var h-full">
        <CardBody className="bg-white relative group/card hover:shadow-2xl hover:shadow-brand-green/[0.1] border-gray-200 w-full h-full rounded-xl border flex flex-col">
          <div 
            className="w-full h-full flex flex-col"
            onMouseEnter={startSlider}
            onMouseLeave={stopSlider}
          >
      <CardItem translateZ="50" className="w-full">
        <Link to={`/products/${product.id}`} className="block relative">
        {/* YENİ: İyileştirilmiş İndirim Rozetleri */}
        <div className="absolute top-2 left-2 z-20 flex flex-col gap-1 max-w-[calc(100%-4rem)]">
          {/* Ürün İndirimi - Kırmızı */}
          {product.discount_percentage > 0 && (
            <div className="relative">
              <div className="bg-gradient-to-r from-red-500 to-red-600 text-white px-3 py-1.5 rounded-lg text-xs font-bold shadow-lg transform hover:scale-105 transition-transform duration-200">
                <div className="flex items-center gap-1">
                  <span className="text-yellow-300">⚡</span>
                  <span>%{Math.round(product.discount_percentage)} ÜRÜN İNDİRİMİ</span>
                </div>
              </div>
              <div className="absolute -bottom-1 left-2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-red-600"></div>
            </div>
          )}
          
          {/* Kategori İndirimi - Mor Gradient */}
          {categoryDiscount && (
            <div className="relative">
              <div 
                className="text-white px-3 py-1.5 rounded-lg text-xs font-bold shadow-lg transform hover:scale-105 transition-all duration-200 backdrop-blur-sm"
                style={{ 
                  background: `linear-gradient(135deg, ${categoryDiscount.badgeColor || '#8b5cf6'}, ${categoryDiscount.badgeColor ? categoryDiscount.badgeColor + '90' : '#a855f7'})` 
                }}
              >
                <div className="flex items-center gap-1">
                  <span className="text-yellow-300">🎁</span>
                  <span>{categoryDiscount.badgeText || 'KAMPANYA'}</span>
                </div>
              </div>
              <div 
                className="absolute -bottom-1 left-2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent"
                style={{ borderTopColor: categoryDiscount.badgeColor || '#8b5cf6' }}
              ></div>
            </div>
          )}
          
          {/* Paket Kampanyası - Turuncu Gradient + Animasyon */}
          {packageDeal && (
            <div className="relative">
              <div 
                className="text-white px-3 py-1.5 rounded-lg text-xs font-bold shadow-lg transform hover:scale-105 transition-all duration-200 animate-pulse"
                style={{ 
                  background: `linear-gradient(135deg, ${packageDeal.badgeColor || '#ff6b35'}, ${packageDeal.badgeColor ? packageDeal.badgeColor + '90' : '#ff8c42'})` 
                }}
              >
                <div className="flex items-center gap-1">
                  <span className="text-yellow-300">📦</span>
                  <span>{packageDeal.badgeText || `${packageDeal.buyCount} Al ${packageDeal.payCount} Öde`}</span>
                </div>
              </div>
              <div 
                className="absolute -bottom-1 left-2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent"
                style={{ borderTopColor: packageDeal.badgeColor || '#ff6b35' }}
              ></div>
            </div>
          )}
          
          {/* Özel Gün Kampanyası - Parıldayan Gradient */}
          {specialDayDiscount && (
            <div className="relative">
              <div 
                className="text-white px-3 py-1.5 rounded-lg text-xs font-bold shadow-lg transform hover:scale-105 transition-all duration-200 animate-bounce"
                style={{ 
                  background: `linear-gradient(135deg, ${specialDayDiscount.badgeColor || '#8b5cf6'}, ${specialDayDiscount.badgeColor ? specialDayDiscount.badgeColor + '90' : '#a855f7'})`,
                  animation: 'sparkle 2s infinite'
                }}
              >
                <div className="flex items-center gap-1">
                  <span className="text-yellow-300 animate-spin">✨</span>
                  <span>{specialDayDiscount.badgeText}</span>
                </div>
              </div>
              <div 
                className="absolute -bottom-1 left-2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent"
                style={{ borderTopColor: specialDayDiscount.badgeColor || '#8b5cf6' }}
              ></div>
            </div>
          )}

          {/* Flash Sale - Kırmızı Gradient */}
          {flashSaleDiscount && (
            <div className="relative">
              <div 
                className="text-white px-3 py-1.5 rounded-lg text-xs font-bold shadow-lg transform hover:scale-105 transition-all duration-200 animate-pulse"
                style={{ 
                  background: `linear-gradient(135deg, ${flashSaleDiscount.badgeColor || '#ff4444'}, ${flashSaleDiscount.badgeColor ? flashSaleDiscount.badgeColor + '90' : '#ff6666'})`,
                  animation: 'flash 1.5s infinite'
                }}
              >
                <div className="flex items-center gap-1">
                  <span className="text-yellow-300">⚡</span>
                  <span>{flashSaleDiscount.badgeText}</span>
                </div>
              </div>
              <div 
                className="absolute -bottom-1 left-2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent"
                style={{ borderTopColor: flashSaleDiscount.badgeColor || '#ff4444' }}
              ></div>
            </div>
          )}
          
          {/* Sepet Kampanyası - Yeşil Gradient (Sadece API'den gelirse) */}
          {cartCampaign && cartCampaign.id && (
            <div className="relative">
              <div 
                className="text-white px-3 py-1.5 rounded-lg text-xs font-bold shadow-lg transform hover:scale-105 transition-all duration-200"
                style={{ 
                  background: `linear-gradient(135deg, ${cartCampaign.badgeColor || '#10b981'}, ${cartCampaign.badgeColor ? cartCampaign.badgeColor + '90' : '#059669'})` 
                }}
              >
                <div className="flex items-center gap-1">
                  <span className="text-yellow-300">🛒</span>
                  <span>{cartCampaign.badgeText}</span>
                </div>
              </div>
              <div 
                className="absolute -bottom-1 left-2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent"
                style={{ borderTopColor: cartCampaign.badgeColor || '#10b981' }}
              ></div>
            </div>
          )}
          

          
          {/* Yeni Ürün Badge'i (Eğer ürün son 7 gün içinde eklendiyse) */}
          {(() => {
            const createdDate = new Date(product.created_at);
            const weekAgo = new Date();
            weekAgo.setDate(weekAgo.getDate() - 7);
            return createdDate > weekAgo;
          })() && (
            <div className="relative">
              <div className="bg-gradient-to-r from-green-500 to-green-600 text-white px-3 py-1.5 rounded-lg text-xs font-bold shadow-lg transform hover:scale-105 transition-transform duration-200">
                <div className="flex items-center gap-1">
                  <span className="text-yellow-300">🆕</span>
                  <span>YENİ</span>
                </div>
              </div>
              <div className="absolute -bottom-1 left-2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-green-600"></div>
            </div>
          )}
        </div>

        {/* Favorite and Quick View Buttons */}
        <div className="absolute top-3 right-3 z-20 flex flex-col gap-2">
          <button onClick={handleFavoriteClick} className="bg-white/70 backdrop-blur-sm p-2 rounded-full transition-transform duration-200 hover:scale-110">
            {isProductFavorited(product.id) ? (
              <HeartIconSolid className="w-6 h-6 text-red-500" />
            ) : (
              <HeartIconOutline className="w-6 h-6 text-gray-600" />
            )}
          </button>
          <button 
            onClick={handleQuickViewClick}
            className="bg-white/70 backdrop-blur-sm p-2 rounded-full transition-all duration-200 hover:scale-110 hover:bg-brand-green hover:text-white group"
            title="Hızlı Bakış"
          >
            <EyeIcon className="w-6 h-6 text-gray-600 group-hover:text-white" />
          </button>
        </div>
        
        {/* --- YENİ SLIDER YAPISI BURAYA DA EKLENDİ --- */}
        <CardItem translateZ="100" className="w-full">
          <div className="relative w-full h-56 sm:h-64 md:h-72 lg:h-80 bg-gray-100 overflow-hidden rounded-t-xl">
          <div 
            className="flex h-full transition-transform duration-700 ease-in-out" // Yavaş ve yumuşak geçiş
            style={{ transform: `translateX(-${currentIndex * 100}%)` }}
          >
            {allImages.length > 0 ? allImages.map((imgUrl, index) => {
              const imageSet = getResponsiveImageSet(imgUrl);
              
              // Debug log - her resim için URL kontrolü
              // console.log(`🖼️ Resim ${index + 1} URL Debug:`, {
              //   originalUrl: imgUrl,
              //   optimizedSrc: imageSet.src,
              //   srcSet: imageSet.srcSet
              // });
              
              return (
                <div key={index} className="w-full h-full flex-shrink-0">
                  <img 
                    src={imageSet.src}
                    srcSet={imageSet.srcSet}
                    sizes={imageSet.sizes}
                    alt={`${product.name} ${index + 1}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width="400"
                    height="400"
                    onError={(e) => {
                      console.error('❌ Resim yüklenemedi:', {
                        src: e.target.src,
                        productName: product.name,
                        originalUrl: imgUrl
                      });
                      // Hata durumunda placeholder göster
                      e.target.src = 'https://placehold.co/400x400/e0e0e0/ffffff?text=Resim+Bulunamadi';
                    }}
                    onLoad={(e) => {
                      // console.log('✅ Resim başarıyla yüklendi:', imageSet.src);
                    }}
                  />
                </div>
              );
            }) : (
              // Eğer hiç resim yoksa yer tutucu göster
              <div className="w-full h-full flex-shrink-0">
                <img 
                  src={getOptimizedImageUrl('https://placehold.co/400x400/e0e0e0/ffffff?text=Resim+Yok', { width: 400, quality: 80 })}
                  alt={product.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  width="400"
                  height="400"
                />
              </div>
            )}
          </div>
          </div>
        </CardItem>
        {/* --- SLIDER YAPISI BİTİŞİ --- */}
      </Link>
      </CardItem>
      <CardItem translateZ="20" className="w-full flex-grow">
        <div className="p-2 sm:p-4 flex flex-col flex-grow">
        <h2 className="text-sm font-bold text-text-dark mb-1 h-10 flex items-center">{product.name}</h2>
        {/* YENİ: İyileştirilmiş Fiyat Gösterimi */}
        <div className="mb-4">
          {(() => {
            const toNum = (v) => {
              const n = typeof v === 'string' ? parseFloat(v.replace(',', '.')) : Number(v);
              return isNaN(n) ? 0 : n;
            };
            
            // Orijinal fiyat ve mevcut fiyat hesaplama
            let originalPrice = toNum(product.original_price);
            let currentPrice = toNum(product.price);
            
            // Eğer original_price yoksa ve discount_percentage varsa, tersine hesapla
            if (!originalPrice && toNum(product.discount_percentage) > 0) {
              originalPrice = currentPrice / (1 - toNum(product.discount_percentage) / 100);
            }
            
            // Eğer hala original_price yoksa, price'ı kullan
            if (!originalPrice) {
              originalPrice = currentPrice;
            }
            
            const categoryDiscountAmount = toNum(categoryDiscount?.discountAmount);
            const flashSaleDiscountAmount = toNum(flashSaleDiscount?.discountAmount);
            
            // Sepet kampanyası indirimi hesapla
            const cartDiscountAmount = cartCampaign ? (() => {
              const discountValue = toNum(cartCampaign.discountValue || cartCampaign.discount_value);
              const discountType = cartCampaign.discountType || cartCampaign.discount_type || 'percentage';
              
              if (discountType === 'percentage') {
                return (currentPrice * discountValue) / 100;
              } else {
                return Math.min(discountValue, currentPrice);
              }
            })() : 0;
            
            // Final fiyat hesaplama - sepet kampanyası dahil
            const finalPrice = Math.max(0, currentPrice - categoryDiscountAmount - flashSaleDiscountAmount - cartDiscountAmount);
            
            const hasProductDiscount = (toNum(product.discount_percentage) > 0) || (originalPrice > currentPrice);
            const hasCategoryDiscount = categoryDiscount && categoryDiscountAmount > 0;
            const hasFlashSaleDiscount = flashSaleDiscount && flashSaleDiscountAmount > 0;
            const hasCartDiscount = cartCampaign && cartDiscountAmount > 0;
            
            // Toplam tasarruf = orijinal fiyat - final fiyat + sepet kampanyası potansiyeli
            const totalSavings = Math.max(0, originalPrice - finalPrice) + cartDiscountAmount;

            const hasAnyDiscount = hasProductDiscount || hasCategoryDiscount || hasFlashSaleDiscount || hasCartDiscount;
            const shouldShowDetailedView = hasAnyDiscount || cartCampaign; // Sepet kampanyası varsa da detaylı görünüm göster

            if (shouldShowDetailedView) {
              return (
                <div className="space-y-2">
                  {/* Ana Fiyat Satırı - Geliştirilmiş */}
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-red-500 text-lg sm:text-xl md:text-2xl font-black">
                      ₺{finalPrice.toFixed(2)}
                    </span>
                    <div className="flex flex-col">
                      <span className="text-gray-400 text-xs sm:text-sm md:text-base line-through decoration-2">
                        ₺{originalPrice.toFixed(2)}
                      </span>
                      {/* İndirim Yüzdesi */}
                      <div className="bg-red-500 text-white px-2 py-0.5 rounded-full text-xs font-bold text-center">
                        %{Math.round(((originalPrice - finalPrice) / originalPrice) * 100)} İNDİRİM
                      </div>
                    </div>
                  </div>
                  
                  {/* İndirim Detayları */}
                  <div className="space-y-1">
                    {hasProductDiscount && (
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                        <span className="text-green-600 text-xs font-medium">
                          Ürün indirimi: ₺{(originalPrice - currentPrice).toFixed(2)}
                        </span>
                      </div>
                    )}
                    {hasCategoryDiscount && (
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: categoryDiscount.badgeColor || '#8b5cf6' }}
                        ></div>
                        <span className="text-purple-600 text-xs font-medium">
                          {categoryDiscount.campaignName}: ₺{categoryDiscountAmount.toFixed(2)}
                        </span>
                      </div>
                    )}
                    {hasFlashSaleDiscount && (
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: flashSaleDiscount.badgeColor || '#ff4444' }}
                        ></div>
                        <span className="text-red-600 text-xs font-medium">
                          ⚡ {flashSaleDiscount.campaignName}: ₺{flashSaleDiscountAmount.toFixed(2)}
                        </span>
                      </div>
                    )}
                    
                    {/* Sepet Kampanyası Bilgisi - Sadece API'den gelirse göster */}
                    {cartCampaign && cartCampaign.id && (
                      <div className="bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-lg p-2 mt-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center">
                              <span className="text-white text-xs">🛒</span>
                            </div>
                            <div>
                              <div className="text-emerald-800 text-xs font-bold">
                                {cartCampaign.badgeText || 'SEPET İNDİRİMİ'}
                              </div>
                              <div className="text-emerald-600 text-xs">
                                {(() => {
                                  const minAmount = cartCampaign.minCartAmount || cartCampaign.min_cart_amount || 0;
                                  const discountVal = cartCampaign.discountValue || cartCampaign.discount_value || 0;
                                  // console.log('🛒 Panel render verileri:', {
                                  //   minAmount,
                                  //   discountVal,
                                  //   campaignData: cartCampaign
                                  // });
                                  return minAmount > 0 ? `${minAmount} TL üzeri %${discountVal} indirim` : `%${discountVal} indirim`;
                                })()}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="bg-emerald-500 text-white px-2 py-1 rounded text-xs font-bold">
                              %{cartCampaign.discountValue || cartCampaign.discount_value || 0}
                            </div>
                            <div className="text-emerald-700 text-xs mt-1">
                              {(() => {
                                const discountValue = cartCampaign.discountValue || cartCampaign.discount_value || 0;
                                const discountType = cartCampaign.discountType || cartCampaign.discount_type || 'percentage';
                                const productPrice = parseFloat(product.price);
                                
                                // Sepet indirimi hesapla
                                const cartDiscount = discountType === 'percentage' 
                                  ? (productPrice * discountValue) / 100
                                  : Math.min(discountValue, productPrice);
                                
                                // console.log('💰 Sepet tasarruf hesaplama:', {
                                //   productPrice,
                                //   discountValue,
                                //   discountType,
                                //   cartDiscount
                                // });
                                
                                return `${cartDiscount.toFixed(2)} TL tasarruf`;
                              })()}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Toplam Tasarruf - Öne Çıkarılmış */}
                    {totalSavings > 0 && (
                      <div className="bg-gradient-to-r from-green-100 to-emerald-100 px-3 py-1.5 rounded-lg border border-green-200">
                        <div className="flex items-center gap-2">
                          <span className="text-green-600">💰</span>
                          <span className="text-green-700 text-sm font-bold">
                            Toplam tasarruf: ₺{totalSavings.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            } else {
              return (
                <div className="space-y-3">
                  <div className="text-center">
                     {cartCampaign ? (
                       <>
                         <p className="text-red-500 text-lg sm:text-xl md:text-2xl font-black">
                           ₺{finalPrice.toFixed(2)}
                         </p>
                         <p className="text-gray-400 text-xs sm:text-sm md:text-base line-through decoration-2">
                           ₺{originalPrice.toFixed(2)}
                         </p>
                         <div className="bg-red-500 text-white px-2 py-0.5 rounded-full text-xs font-bold text-center mt-1">
                           %{Math.round(((originalPrice - finalPrice) / originalPrice) * 100)} İNDİRİM
                         </div>
                       </>
                     ) : (
                       <>
                    <p className="text-brand-green text-base sm:text-lg md:text-xl font-black">
                      ₺{parseFloat(product.price).toFixed(2)}
                    </p>
                    <p className="text-gray-500 text-sm mt-1">En iyi fiyat garantisi</p>
                       </>
                     )}
                  </div>
                  
                  {/* Sepet Kampanyası Bilgisi - Sadece Gerçek Kampanya Varsa Göster */}
                  {/* console.log('🛒 ProductCard Simple View: cartCampaign state:', cartCampaign) */}
                  {cartCampaign && (
                    <div className="bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50 border-2 border-emerald-200 rounded-xl p-3 shadow-lg hover:shadow-xl transition-all duration-300">
                      {/* Başlık ve Ana İndirim */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-green-600 rounded-full flex items-center justify-center shadow-md">
                            <span className="text-white text-sm font-bold">🛒</span>
                          </div>
                          <div>
                            <div className="text-emerald-800 text-sm font-extrabold">
                              {cartCampaign ? (cartCampaign.badgeText || 'SEPET İNDİRİMİ') : 'SEPET İNDİRİMİ'}
                            </div>
                            <div className="text-emerald-600 text-xs font-medium">
                              {cartCampaign ? 
                                (cartCampaign.minCartAmount ? `${cartCampaign.minCartAmount} TL üzeri alışverişte` : 'Tüm alışverişlerde') :
                                'Tüm alışverişlerde'
                              }
                            </div>
                          </div>
                        </div>
                        <div className="bg-gradient-to-r from-emerald-600 to-green-600 text-white px-3 py-1.5 rounded-full font-black text-sm shadow-md">
                          {cartCampaign ? 
                            (cartCampaign.discountType === 'percentage' ? `%${cartCampaign.discountValue}` : `${cartCampaign.discountValue} TL`) :
                            '%10'
                          }
                        </div>
                      </div>
                      
                      {/* Detaylı Fiyat Hesabı */}
                      <div className="bg-white bg-opacity-80 rounded-lg p-3 border border-emerald-100">
                        <div className="text-xs text-emerald-700 font-bold mb-2 flex items-center gap-1">
                          <span className="text-yellow-500">💰</span>
                          Bu ürünü sepete eklerseniz:
                        </div>
                        
                        <div className="space-y-2">
                          {/* Ürün Fiyatı */}
                          <div className="flex items-center justify-between bg-gray-50 rounded px-2 py-1">
                            <span className="text-xs text-gray-700 font-medium">Bu ürün fiyatı:</span>
                            <span className="text-xs font-bold text-gray-900">{parseFloat(product.price).toFixed(2)} TL</span>
                          </div>
                          
                          {/* Sepet İndirimi */}
                          <div className="flex items-center justify-between bg-emerald-100 rounded px-2 py-1">
                            <span className="text-xs text-emerald-700 font-medium">Sepet indirimi:</span>
                            <span className="text-emerald-700 font-black text-xs">
                              {(() => {
                                const discountValue = cartCampaign ? cartCampaign.discountValue : 10;
                                const discountType = cartCampaign ? cartCampaign.discountType : 'percentage';
                                const productPrice = parseFloat(product.price);
                                
                                return discountType === 'percentage' 
                                  ? `-${((productPrice * discountValue) / 100).toFixed(2)} TL`
                                  : `-${Math.min(discountValue, productPrice).toFixed(2)} TL`;
                              })()}
                            </span>
                          </div>
                          
                          {/* Toplam Tasarruf Potansiyeli */}
                          <div className="bg-gradient-to-r from-yellow-100 to-amber-100 border border-yellow-300 rounded-lg px-3 py-2 mt-2">
                            <div className="flex items-center justify-between">
                              <span className="text-amber-800 font-bold text-sm flex items-center gap-1">
                                <span className="text-yellow-500">⭐</span>
                                TASARRUF POTANSİYELİ
                              </span>
                              <span className="text-amber-900 font-black text-lg">
                                {(() => {
                                  const discountValue = cartCampaign ? cartCampaign.discountValue : 10;
                                  const discountType = cartCampaign ? cartCampaign.discountType : 'percentage';
                                  const productPrice = parseFloat(product.price);
                                  
                                  const cartDiscount = discountType === 'percentage' 
                                    ? (productPrice * discountValue) / 100
                                    : Math.min(discountValue, productPrice);
                                  return `${cartDiscount.toFixed(2)} TL`;
                                })()}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Teşvik Mesajı */}
                        <div className="mt-2 text-center">
                          <div className="text-xs text-emerald-600 bg-emerald-100 rounded-full px-3 py-1 inline-block font-medium">
                            🎯 Şimdi sepete ekleyin, kampanyadan yararlanın!
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            }
          })()}
        </div>
        <CardItem translateZ="40" className="w-full mt-auto">
          <button 
            onClick={(e) => handleProductClick(e, product.id)}
            className="w-full bg-brand-green-light text-white font-bold py-2 px-4 rounded-lg hover:bg-brand-green transition-colors flex items-center justify-center gap-2"
          >
            İncele
            <ArrowRightIcon className="w-5 h-5"/>
          </button>
        </CardItem>
        </div>
      </CardItem>
          </div>
        </CardBody>
      </CardContainer>
    </>
  );
};

export default ProductCard;